import { useState } from 'react'
import { conciliacionBancariaWorkflow } from '../../services/api'
import FileList from '../../components/FileList/FileList'
import styles from './ConciliacionBancaria.module.css'

function ConciliacionBancaria() {
    const [formData, setFormData] = useState({
        banco: 'Generico',
        extractoFile: null,
        auxiliarFile: null,
        invertirSignos: false,
        maxCombinaciones: 1000
    })
    const [isProcessing, setIsProcessing] = useState(false)
    const [result, setResult] = useState(null)

    const bancos = ['Generico', 'BBVA', 'Bogotá', 'Davivienda', 'Bancolombia']

    const handleFileChange = (e, fileType) => {
        const file = e.target.files[0]
        if (file) {
            setFormData({
                ...formData,
                [fileType]: file
            })
        }
    }

    const handleSubmit = async (e) => {
        e.preventDefault()
        setIsProcessing(true)
        setResult(null)

        try {
            console.log('Iniciando flujo de conciliación bancaria...')

            const resultado = await conciliacionBancariaWorkflow({
                extractoFile: formData.extractoFile,
                auxiliarFile: formData.auxiliarFile,
                banco: formData.banco,
                invertirSignos: formData.invertirSignos,
                maxCombinaciones: formData.maxCombinaciones
            })

            console.log('Conciliación completada:', resultado)

            if (resultado.success) {
                setResult({
                    totalMovimientos: resultado.statistics?.totalMovimientos || 0,
                    conciliados: resultado.statistics?.conciliados || 0,
                    noConciliados: resultado.statistics?.noConciliados || 0,
                    porcentajeConciliado: resultado.statistics?.porcentajeConciliado || 0,
                    downloadUrls: resultado.downloadUrls || {},
                    metadata: resultado.metadata || {}
                })
            } else {
                throw new Error(resultado.error || 'Error en conciliación')
            }
        } catch (error) {
            console.error('Error en conciliación bancaria:', error)
            alert(`Error: ${error.message || 'No se pudo procesar la conciliación'}`)
        } finally {
            setIsProcessing(false)
        }
    }

    const isFormValid = formData.banco && formData.extractoFile && formData.auxiliarFile

    return (
        <div className={styles.page}>
            <div className={styles.container}>
                {/* Header */}
                <div className={styles.header}>
                    <h1 className={styles.title}>🏦 Conciliación Bancaria</h1>
                    <p className={styles.subtitle}>
                        Concilia automáticamente extractos bancarios con libros auxiliares contables
                    </p>
                </div>

                {/* Upload Form */}
                <div className={styles.formCard}>
                    <form onSubmit={handleSubmit} className={styles.form}>
                        <div className={styles.formGroup}>
                            <label htmlFor="banco" className={styles.label}>
                                Banco *
                            </label>
                            <select
                                id="banco"
                                value={formData.banco}
                                onChange={(e) => setFormData({ ...formData, banco: e.target.value })}
                                className={styles.select}
                            >
                                {bancos.map((banco) => (
                                    <option key={banco} value={banco}>
                                        {banco}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div className={styles.fileUploads}>
                            <div className={styles.uploadBox}>
                                <label htmlFor="extractoFile" className={styles.uploadLabel}>
                                    <div className={styles.uploadIcon}>🏦</div>
                                    <div className={styles.uploadText}>
                                        <strong>Extracto Bancario</strong>
                                        <span>Selecciona archivo Excel (.xlsx, .xls)</span>
                                    </div>
                                    {formData.extractoFile && (
                                        <div className={styles.fileName}>✓ {formData.extractoFile.name}</div>
                                    )}
                                </label>
                                <input
                                    type="file"
                                    id="extractoFile"
                                    accept=".xlsx,.xls"
                                    onChange={(e) => handleFileChange(e, 'extractoFile')}
                                    className={styles.fileInput}
                                />
                            </div>

                            <div className={styles.uploadBox}>
                                <label htmlFor="auxiliarFile" className={styles.uploadLabel}>
                                    <div className={styles.uploadIcon}>📗</div>
                                    <div className={styles.uploadText}>
                                        <strong>Libro Auxiliar</strong>
                                        <span>Selecciona archivo Excel (.xlsx, .xls)</span>
                                    </div>
                                    {formData.auxiliarFile && (
                                        <div className={styles.fileName}>✓ {formData.auxiliarFile.name}</div>
                                    )}
                                </label>
                                <input
                                    type="file"
                                    id="auxiliarFile"
                                    accept=".xlsx,.xls"
                                    onChange={(e) => handleFileChange(e, 'auxiliarFile')}
                                    className={styles.fileInput}
                                />
                            </div>
                        </div>

                        <div className={styles.checkboxGroup}>
                            <input
                                type="checkbox"
                                id="invertirSignos"
                                checked={formData.invertirSignos}
                                onChange={(e) => setFormData({ ...formData, invertirSignos: e.target.checked })}
                                className={styles.checkbox}
                            />
                            <label htmlFor="invertirSignos" className={styles.checkboxLabel} style={{ marginLeft: '8px', cursor: 'pointer', color: '#000' }}>
                                Invertir signos de débitos/créditos (Solo si es necesario)
                            </label>
                        </div>

                        <div className={styles.helpText} style={{ fontSize: '0.85rem', color: '#666', marginTop: '8px', padding: '8px', backgroundColor: '#f8f9fa', borderRadius: '4px' }}>
                            ℹ️ <strong>¿Cuándo usar "Invertir signos"?</strong><br />
                            Marca esta opción solo si los débitos y créditos en tu extracto bancario tienen signos invertidos.
                        </div>

                        {/* Configuración avanzada - Checkbox para habilitar */}
                        <div style={{ marginTop: '16px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
                                <input
                                    type="checkbox"
                                    id="enableMaxCombinaciones"
                                    checked={formData.enableMaxCombinaciones || false}
                                    onChange={(e) => setFormData({ ...formData, enableMaxCombinaciones: e.target.checked })}
                                    style={{ marginRight: '8px', cursor: 'pointer' }}
                                />
                                <label htmlFor="enableMaxCombinaciones" style={{ fontWeight: '500', color: '#000', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    ⚙️ Configuración avanzada de búsqueda
                                    <span
                                        title="Habilita esta opción para controlar cuántas combinaciones de registros se prueban durante la conciliación. Valores mayores pueden encontrar más conciliaciones pero toman más tiempo."
                                        style={{ cursor: 'help', fontSize: '1.2rem', color: '#666' }}
                                    >
                                        ❓
                                    </span>
                                </label>
                            </div>

                            {formData.enableMaxCombinaciones && (
                                <div style={{ padding: '12px', backgroundColor: '#f0f8ff', borderRadius: '4px', border: '1px solid #d0e8ff', marginLeft: '24px' }}>
                                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: '#000' }}>
                                        Máximo de combinaciones a probar:
                                    </label>
                                    <input
                                        type="number"
                                        value={formData.maxCombinaciones || 1000}
                                        onChange={(e) => setFormData({ ...formData, maxCombinaciones: parseInt(e.target.value) || 1000 })}
                                        min="100"
                                        max="50000"
                                        step="100"
                                        style={{ width: '150px', padding: '8px', borderRadius: '4px', border: '1px solid #ccc' }}
                                    />
                                    <div style={{ fontSize: '0.8rem', color: '#666', marginTop: '4px' }}>
                                        💡 <strong>Valores sugeridos:</strong><br />
                                        • 1000 = Rápido (86% conciliación típica)<br />
                                        • 5000 = Balanceado (90-95% conciliación)<br />
                                        • 10000+ = Exhaustivo (máxima precisión, más lento)
                                    </div>
                                </div>
                            )}
                        </div>

                        <button
                            type="submit"
                            disabled={!isFormValid || isProcessing}
                            className={styles.submitBtn}
                        >
                            {isProcessing ? (
                                <>
                                    <span className="spinner"></span>
                                    Conciliando... (Puede tomar 1-2 minutos)
                                </>
                            ) : (
                                'Iniciar Conciliación'
                            )}
                        </button>
                    </form>
                </div>

                {/* Results */}
                {result && (
                    <div className={styles.resultsCard}>
                        <h2 className={styles.resultsTitle}>✓ Conciliación Completada</h2>

                        <div className={styles.progressBar}>
                            <div
                                className={styles.progressFill}
                                style={{ width: `${result.porcentajeConciliado}%` }}
                            >
                                {result.porcentajeConciliado}% Conciliado
                            </div>
                        </div>

                        <div className={styles.statsGrid}>
                            <div className={styles.statBox}>
                                <div className={styles.statValue}>{result.totalMovimientos}</div>
                                <div className={styles.statLabel}>Total Movimientos</div>
                            </div>
                            <div className={styles.statBox}>
                                <div className={styles.statValue} style={{ color: 'var(--color-accent-success)' }}>
                                    {result.conciliados}
                                </div>
                                <div className={styles.statLabel}>Conciliados</div>
                            </div>
                            <div className={styles.statBox}>
                                <div className={styles.statValue} style={{ color: 'var(--color-accent-warning)' }}>
                                    {result.noConciliados}
                                </div>
                                <div className={styles.statLabel}>No Conciliados</div>
                            </div>
                        </div>

                        <div className={styles.downloadsSection}>
                            <a
                                href={result.downloadUrls.completo}
                                download
                                className={styles.downloadBtn}
                            >
                                📥 Descargar Resultados Completos (Excel)
                            </a>
                            <a
                                href={result.downloadUrls.resumen}
                                download
                                className={`${styles.downloadBtn} ${styles.downloadBtnSecondary}`}
                            >
                                📄 Descargar Resumen de Conciliación (Excel)
                            </a>
                        </div>
                    </div>
                )}

                {/* File List - Mostrar archivos procesados */}
                <div className={styles.fileListSection}>
                    <h2 className={styles.sectionTitle}>📂 Mis Conciliaciones</h2>
                    <FileList folderFilter="conciliacion-bancaria" />
                </div>
            </div>
        </div>
    )
}

export default ConciliacionBancaria
